import { Bar, CartesianGrid, ComposedChart, Line, Tooltip, XAxis, YAxis, BarChart } from 'recharts'
import { AlertTriangle, CheckCircle2, Clock, Factory, Layers, ListChecks, Percent } from 'lucide-react'
import { formatDate } from '@/lib/date'
import { BAR_RADIUS, CHART, gridProps, tooltipProps, xAxisProps, yAxisProps } from '@/lib/chart'
import { cn } from '@/lib/cn'
import type { OverviewReport } from '@/types'
import Card, { SectionLabel } from '@/components/ui/Card'
import StatTile from '@/components/ui/StatTile'
import { ChartBox, ChartEmpty, ChartLegend } from '@/components/ui/Chart'
import { Table, Row, Cell, EmptyRow } from '@/components/ui/Table'

const icon = 'size-5'

export default function OverviewTab({ data, range }: { data: OverviewReport; range: { from: string; to: string } }) {
  const k = data.kpis
  const hasActivity = data.daily.some((d) => d.pieces || d.stages || d.issues)

  return (
    <>
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatTile label="Jobs completed" value={k.jobs_completed} icon={<Factory className={icon} />} tone="primary" />
        <StatTile label="Pieces completed" value={k.pieces_completed} icon={<Layers className={icon} />} tone="pending" />
        <StatTile label="Stages completed" value={k.stages_completed} icon={<ListChecks className={icon} />} tone="success" />
        <StatTile label="Issues flagged" value={k.issues_flagged} icon={<AlertTriangle className={icon} />} tone={k.issues_flagged > 0 ? 'danger' : 'neutral'} />
        <StatTile label="Open issues now" value={k.issues_open_now} icon={<AlertTriangle className={icon} />} tone={k.issues_open_now > 0 ? 'danger' : 'neutral'} to="/admin/issues" hint="live, all time" />
        <StatTile label="Avg turnaround" value={k.avg_turnaround_days === null ? '-' : `${k.avg_turnaround_days} days`} icon={<Clock className={icon} />} tone="warning" hint="first start to last complete" />
        <StatTile label="On time" value={k.on_time_pct === null ? '-' : `${k.on_time_pct}%`} icon={<Percent className={icon} />} tone={k.on_time_pct !== null && k.on_time_pct < 80 ? 'danger' : 'success'} hint="finished by the due date" />
        <StatTile label="Sections active" value={data.departments.filter((d) => d.jobs_completed > 0).length} icon={<CheckCircle2 className={icon} />} tone="neutral" hint={`of ${data.departments.length}`} />
      </div>

      <div className="mt-6 grid gap-4 lg:grid-cols-3">
        <Card className="min-w-0 lg:col-span-2">
          <SectionLabel>Pieces and stages per day</SectionLabel>
          {hasActivity ? (
            <>
              <ChartBox>
                <ComposedChart data={data.daily}>
                  <CartesianGrid {...gridProps} />
                  <XAxis dataKey="label" {...xAxisProps} />
                  <YAxis yAxisId="left" {...yAxisProps} />
                  <YAxis yAxisId="right" orientation="right" {...yAxisProps} />
                  <Tooltip {...tooltipProps} />
                  <Bar yAxisId="left" dataKey="pieces" name="Pieces" fill={CHART.primary} radius={BAR_RADIUS} />
                  <Line yAxisId="right" type="monotone" dataKey="stages" name="Stages" stroke={CHART.pending} strokeWidth={2} dot={false} />
                </ComposedChart>
              </ChartBox>
              <ChartLegend items={[{ label: 'Pieces completed (left)', fill: CHART.primary }, { label: 'Stages completed (right)', fill: CHART.pending }]} />
            </>
          ) : (
            <ChartEmpty />
          )}
        </Card>

        <Card className="min-w-0">
          <SectionLabel>Pieces per section</SectionLabel>
          <ChartBox>
            <BarChart data={data.departments}>
              <CartesianGrid {...gridProps} />
              <XAxis dataKey="department" {...xAxisProps} tick={{ fontSize: 10, fill: CHART.tick }} interval={0} />
              <YAxis {...yAxisProps} />
              <Tooltip {...tooltipProps} />
              <Bar dataKey="pieces" name="Pieces" fill={CHART.primary} radius={BAR_RADIUS} />
            </BarChart>
          </ChartBox>
        </Card>
      </div>

      <SectionLabel className="mt-6">Daily breakdown ({range.from} to {range.to})</SectionLabel>
      <Table columns={['Date', 'Pieces completed', 'Stages completed', 'Issues']}>
        {data.daily.filter((d) => d.pieces || d.stages || d.issues).map((d) => (
          <Row key={d.date}>
            <Cell className="font-semibold">{formatDate(d.date)}</Cell>
            <Cell className="font-semibold">{d.pieces}</Cell>
            <Cell>{d.stages}</Cell>
            <Cell className={cn(d.issues > 0 && 'font-semibold text-status-danger')}>{d.issues}</Cell>
          </Row>
        ))}
        {!hasActivity && <EmptyRow colSpan={4}>No stage activity logged in this date range.</EmptyRow>}
      </Table>
    </>
  )
}
